/* Libraries */
import React, {memo} from 'react';
import dynamic from 'next/dynamic';

/* Query Cache/Selectors */
import {useIsSessionLoadedSelector, useUserHasSubscriptionSelector, useUserIdSelector} from '@store/queries/user/useUser.selector';
import usePartnerCache from '@integrations/partners/usePartnerQuery.cache';

/* Components */
import {Grid, Button, BUTTON_THEMES} from 'ht-styleguide';
import StarRating from '@components/UI/StarRating';
import {ServiceHeaderTrigger} from '@scenes/ServiceSkuPage/Parts/ServiceHeader';
import {ServiceBookNowTrigger} from '@scenes/ServiceSkuPage/Parts/ServiceBookNow';
import Either from '@components/Either';
import Maybe from '@components/Maybe';
import SpaceWithLoader from '@components/UI/Space/Space.withLoader';
import {ZipValidationState} from '@components/Sku/ZipCodeEntry/ZipCodeEntry';
import SpaceY from '@components/UI/Space/Space.y-axis';
import RenderHtml from '@scenes/LandingsPage/Components/RenderHtml';

/* Local Client Store */
import useServiceSkuStore from '@scenes/ServiceSkuPage/store/store.servicesku';

/* Types */
import {SkuExtended} from 'types/sku.types';
import ServiceInfoOptions from './ServiceInfoOptions';
import styles from './serviceinfo.module.scss';

/* Components dynamic */
const ZipCodeEntryDynamic = dynamic(() => import('@components/Sku/ZipCodeEntry'), {ssr: false});
const ServicePriceDynamic = dynamic(() => import('@scenes/ServiceSkuPage/Parts/ServicePrice'), {ssr: false});
const DevlinkCustomHtmlBlockDynamic = dynamic(() => import('@devlink/DevlinkCustomHtmlBlock').then(mod => mod.DevlinkCustomHtmlBlock), {ssr: false});
const DevlinkCheckedListDyanmic = dynamic(() => import('@devlink/DevlinkCheckedList').then(module => module.DevlinkCheckedList), {ssr: false});
const DevlinkCheckedListItemDynamic = dynamic(() => import('@devlink/DevlinkCheckedListItem').then(module => module.DevlinkCheckedListItem), {
  ssr: false,
});

type ServiceInfoDetailsProps = {
  sku: SkuExtended;
  onBook: BaseAnyFunction;
  onRating: BaseAnyFunction;
  onUpsellToggle: BaseAnyFunction;
  onUpsellMore: BaseAnyFunction;
  className?: string;
  isPending?: boolean;
};

const ServiceInfoDetails = ({sku, onBook, onRating, onUpsellToggle, onUpsellMore, className, isPending}: ServiceInfoDetailsProps) => {
  const {rating, ctaText, name = '', showZipCodeTest} = sku || {};
  const userLoaded = useIsSessionLoadedSelector();

  /* Store */
  const {zipValidationState} = useServiceSkuStore(state => state.zipCodeGate);
  const hasSubscription = useUserHasSubscriptionSelector();
  const userId = useUserIdSelector();

  /* Query */
  const partner = usePartnerCache();

  /* Methods */
  const determineToShowButton = () => (zipValidationState === ZipValidationState.SUCCESS && !showZipCodeTest) || hasSubscription || partner?.id;

  /* Constants */
  const isPartner = partner?.id;

  /* Heights for CLS */
  // eslint-disable-next-line no-nested-ternary
  const heightM = showZipCodeTest ? 290 : isPartner || userId ? 10 : 285;
  // eslint-disable-next-line no-nested-ternary
  const heightD = showZipCodeTest ? 235 : isPartner || userId ? 10 : 225;

  return (
    <Grid.Fluid classes={className} dataTestId="sku-services-info-details">
      <Grid.Row>
        <Grid.Column sm={4} md={8} lg={12}>
          <h1 className="h3">{name}</h1>
          <Maybe condition={hasSubscription || isPartner}>
            <ServicePriceDynamic sku={sku} onInfo={onUpsellMore} className={styles.price} />
          </Maybe>
          <Maybe condition={rating}>
            <StarRating stars={sku.rating?.rating} count={sku.rating?.ratingCount || 0} onClick={onRating} className={styles.rating} size="sm" />
          </Maybe>
        </Grid.Column>

        <Grid.Column sm={4} md={8} lg={12} classes={styles.box}>
          {/*
              I admit this is a hoop, verifying our session is ready, but we are searching for perf and SSG helps.
              We can possibly solve this with server side components. V12 buggy atm, so we'll wait a few weeks.
              So we conditional load these goofy - do this,else do that, then do that if this then that.
          */}
          <Maybe condition={!hasSubscription}>
            <Either condition={userLoaded && !isPending}>
              <div className="service_button_upsell_div">
                <ZipCodeEntryDynamic sku={sku} onUpsellMore={onUpsellMore} onUpsellToggle={onUpsellToggle} />
              </div>
              <Maybe condition={!partner?.id}>
                <SpaceWithLoader verticalM={heightM} verticalD={heightD} loader />
              </Maybe>
            </Either>
          </Maybe>
          <ServiceHeaderTrigger />
          <ServiceBookNowTrigger />
          <Maybe condition={determineToShowButton()}>
            <Button theme={BUTTON_THEMES.PRIMARY} className={styles.button} onClick={onBook}>
              {ctaText}
            </Button>
          </Maybe>
          <Maybe condition={sku?.content?.description && sku.isLandingPage}>
            <SpaceY classes="paddingTop-medium" />
            <DevlinkCustomHtmlBlockDynamic customHtml={<RenderHtml description={sku?.content?.description} />} />
          </Maybe>
          <Maybe condition={!sku?.content?.description && sku.isLandingPage && sku.skuBullets?.length > 0}>
            <SpaceY classes="paddingTop-medium" />
            <DevlinkCheckedListDyanmic
              showTitle
              title="Service includes:"
              checkedList={sku.skuBullets?.map((item: {bullet: string}) => <DevlinkCheckedListItemDynamic text={item.bullet} />)}
            />
          </Maybe>
          <Maybe condition={!sku.isLandingPage}>
            <ServiceInfoOptions options={sku.skuBullets} className={styles.options} />
          </Maybe>
        </Grid.Column>
      </Grid.Row>
      <style jsx>{`
        .service_button_upsell_div {
          min-height: ${heightM}px;

          @media (min-width: 576px) {
            min-height: ${heightD}px;
          }
        }
        .service_display__ready {
          visibility: ${!userLoaded ? 'hidden' : 'visible'};
        }
      `}</style>
    </Grid.Fluid>
  );
};
export default memo(ServiceInfoDetails);
